import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ZonePreset } from '@ixicare/resources';
import { t } from '~/i18n';
import { useStores } from '~store/utils';

const zonePresetsKey = (siteId?: number) => ['zonePresets', siteId] as const;

export function useZonePresets(siteId?: number) {
  return useQuery({
    queryKey: zonePresetsKey(siteId),
    queryFn: () => ZonePreset.getZonePresets({ siteId }),
    enabled: typeof siteId === 'number',
  });
}

export function useZonePresetMutations(siteId: number) {
  const queryClient = useQueryClient();
  const { notifications } = useStores();

  const notify = (notificationType: 'success' | 'error', key: string) =>
    notifications.addUserActionNotification({ notificationType, message: t(key) });

  // Care groups read their zones through the preset they follow, so a preset that changes leaves
  // every group's cached zone list stale - and deleting one detaches the groups that were on it.
  // Both are invalidated by prefix, since the groups cache is keyed by site uuid rather than id.
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: zonePresetsKey(siteId) });
    queryClient.invalidateQueries({ queryKey: ['hcpGroups'] });
  };

  const create = useMutation({
    mutationFn: (input: { name: string; zoneIds: string[] }) => ZonePreset.createZonePreset({ siteId, ...input }),
    onSuccess: () => {
      invalidate();
      notify('success', 'zonePresets.create.success');
    },
    onError: () => notify('error', 'zonePresets.create.error'),
  });

  const update = useMutation({
    mutationFn: ({ presetId, ...changes }: { presetId: string; name?: string; zoneIds?: string[] }) =>
      ZonePreset.patchZonePreset(presetId, changes),
    onSuccess: () => {
      invalidate();
      notify('success', 'zonePresets.update.success');
    },
    onError: () => notify('error', 'zonePresets.update.error'),
  });

  const remove = useMutation({
    mutationFn: (presetId: string) => ZonePreset.deleteZonePreset(presetId),
    onSuccess: () => {
      invalidate();
      notify('success', 'zonePresets.delete.success');
    },
    onError: () => notify('error', 'zonePresets.delete.error'),
  });

  return { create, update, remove };
}
